import React, {useEffect, useRef} from 'react';
import {XIcon} from '../Icons';

interface ModalProps {
  isOpen: boolean;
  title: string;
  /** Sits under the title in muted text — the "Original file: …" line and friends. */
  subtitle?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  /** Rendered right-aligned in the sticky footer. */
  footer?: React.ReactNode;
  widthClass?: string;
  /**
   * Set false for a dialog the user must resolve from inside it — no close button, Escape and a
   * backdrop click do nothing. `onClose` still has to be passed but is never called. Used for gates
   * like the first-login video editor question, where there is deliberately no way out except
   * answering it.
   */
  dismissible?: boolean;
}

/**
 * The one dialog shell the workspace uses.
 *
 * The desktop app hand-rolled a backdrop per modal, which is how they drifted apart. This one owns
 * the behaviours that are easy to forget: Escape closes, a backdrop click closes, the page behind
 * cannot scroll, and focus lands inside the dialog rather than staying on the button that opened it.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  title,
  subtitle,
  onClose,
  children,
  footer,
  widthClass = 'max-w-lg',
  dismissible = true,
}) => {
  const panelRef = useRef<HTMLDivElement>(null);

  // Held in a ref so the effect below keys off `isOpen` alone. Callers routinely pass an inline
  // arrow for `onClose`, and depending on it would re-run the effect on every render — which means
  // re-focusing the first field while the user is typing in the second.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && dismissible) {
        onCloseRef.current();
      }
    };

    document.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    // The first focusable control, so a keyboard user is already inside the dialog.
    panelRef.current
      ?.querySelector<HTMLElement>('input, select, textarea, button:not([data-modal-close])')
      ?.focus();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isOpen, dismissible]);

  if (!isOpen) {
    return null;
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-950/80 backdrop-blur-sm"
      onMouseDown={(event) => {
        if (dismissible && event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        className={`w-full ${widthClass} max-h-[90vh] flex flex-col rounded-xl bg-gray-900
         border border-gray-800 shadow-2xl shadow-black/50`}
      >
        <div className="flex items-start justify-between gap-4 px-5 pt-5 pb-4 border-b border-gray-800">
          <div className="min-w-0">
            <h2 className="text-base font-semibold text-gray-50">{title}</h2>
            {subtitle && <div className="mt-1 text-sm text-gray-400">{subtitle}</div>}
          </div>
          {dismissible && (
            <button
              type="button"
              data-modal-close
              onClick={onClose}
              aria-label="Close"
              className="-mr-1 -mt-1 p-1.5 rounded-lg text-gray-500 hover:text-gray-200 hover:bg-gray-800
               transition-colors cursor-pointer"
            >
              <XIcon className="w-4 h-4" />
            </button>
          )}
        </div>

        <div className="px-5 py-4 overflow-y-auto">{children}</div>

        {footer && (
          <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-800">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
};

export default Modal;
